import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdFlight,
  MdInfo,
  MdSettings,
  MdImage,
  MdPictureAsPdf,
  MdDescription,
  MdPerson,
  MdBusiness,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected", 3: "Expired" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626", 3: "#6B7280" };

const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div
      style={{
        width: 42,
        height: 42,
        border: "4px solid #CBD5E1",
        borderTopColor: "#2563EB",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 220px 1fr",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={22} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFFAA",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg,#3B82F6 0%,#60A5FA 50%,#93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: 6,
      }}
    >
      <Icon size={22} />
      <strong>{title}</strong>
    </div>
    {children}
  </div>
);

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.aircraftsaleleaseAvailable.confirmStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Confirm Status</div>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={{ width: "100%", height: 36, marginBottom: 16 }}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const AircraftSaleLeaseAvailableDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    api.aircraftsaleleaseAvailable.getById(id).then((r: any) => {
      setData(r?.data?.data ?? r?.data);
      setLoading(false);
    });
  }, [id]);

  return (
    <Modal open fullScreen onClose={onClose}>
      {loading ? (
        <Loader />
      ) : (
        <div style={{ padding: 24 }}>
          <h1 style={{ fontSize: 22, marginBottom: 24 }}>{data.headline}</h1>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
              gap: 20,
            }}
          >
            <Card icon={MdDescription} title="General">
              <InfoRow
                icon={MdInfo}
                label="Trade Type"
                value={data.tradeType}
              />
              <InfoRow
                icon={MdInfo}
                label="Confirm Status"
                value={
                  <Badge
                    label={ConfirmStatusText[data.confirmStatus]}
                    color={ConfirmStatusColor[data.confirmStatus]}
                  />
                }
              />
              <InfoRow
                icon={MdInfo}
                label="Priority"
                value={<BoolBadge value={data.isPriority} />}
              />
              <InfoRow
                icon={MdInfo}
                label="Priority Expire"
                value={
                  data.priorityEXpireDate &&
                  new Date(data.priorityEXpireDate).toLocaleDateString()
                }
              />
            </Card>

            <Card icon={MdFlight} title="Aircraft">
              <InfoRow
                icon={MdFlight}
                label="Aircraft Type"
                value={data.aircraftType?.name}
              />
              <InfoRow icon={MdSettings} label="YOM" value={data.yom} />
              <InfoRow icon={MdSettings} label="MSN" value={data.msn} />
              <InfoRow
                icon={MdSettings}
                label="Capacity"
                value={data.capacity}
              />
              <InfoRow
                icon={MdSettings}
                label="Condition"
                value={data.condition}
              />
              <InfoRow icon={MdSettings} label="MTOW" value={data.mtow} />
            </Card>

            <Card icon={MdSettings} title="Engine">
              <InfoRow
                icon={MdSettings}
                label="Engine Type"
                value={data.engineType?.code}
              />
              <InfoRow
                icon={MdSettings}
                label="Total Flight Hour"
                value={data.totalFlightHour}
              />
              <InfoRow
                icon={MdSettings}
                label="Total Flight Cycle"
                value={data.totalFlightCycle}
              />
              <InfoRow
                icon={MdSettings}
                label="Engine First Limiter"
                value={data.engineFirstLimiterCycle}
              />
            </Card>

            <Card icon={MdSettings} title="Sale / Lease">
              <InfoRow icon={MdInfo} label="Owner" value={data.ownerName} />
              <InfoRow
                icon={MdInfo}
                label="Asking Price"
                value={data.askingPrice}
              />
              <InfoRow
                icon={MdInfo}
                label="Availability Date"
                value={
                  data.availabilityDate &&
                  new Date(data.availabilityDate).toLocaleDateString()
                }
              />
              <InfoRow icon={MdInfo} label="Lessor" value={data.lessorName} />
              <InfoRow
                icon={MdInfo}
                label="Lease Start"
                value={
                  data.leaseStartDate &&
                  new Date(data.leaseStartDate).toLocaleDateString()
                }
              />
              <InfoRow
                icon={MdInfo}
                label="Lease Duration (Month)"
                value={data.leaseDurationMonth}
              />
              <InfoRow
                icon={MdInfo}
                label="Monthly Lease Rate"
                value={data.monthlyLeaseRateUsd}
              />
            </Card>

            <Card icon={MdInfo} title="Location">
              <InfoRow
                icon={MdInfo}
                label="Continent"
                value={data.location?.continent?.name}
              />
              <InfoRow
                icon={MdInfo}
                label="Country"
                value={data.location?.country?.name}
              />
              <InfoRow
                icon={MdInfo}
                label="City"
                value={data.location?.city?.name}
              />
              <InfoRow
                icon={MdFlight}
                label="Airport"
                value={
                  data.airport
                    ? `${data.airport.name} (${data.airport.codeIATA})`
                    : "-"
                }
              />
            </Card>

            {data.description && (
              <Card icon={MdDescription} title="Description">
                <div
                  style={{
                    fontSize: 13,
                    color: "#374151",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    padding: "4px 0",
                  }}
                >
                  {data.description}
                </div>
              </Card>
            )}

            <Card icon={MdPerson} title="User">
              <InfoRow
                icon={MdPerson}
                label="Username"
                value={data.user?.username}
              />
              <InfoRow icon={MdInfo} label="Email" value={data.user?.email} />
              <InfoRow
                icon={MdInfo}
                label="Phone"
                value={data.user?.phoneNumber}
              />
            </Card>

            <Card icon={MdBusiness} title="Company">
              <InfoRow
                icon={MdBusiness}
                label="Company"
                value={data.company?.companyName}
              />
            </Card>
          </div>

          <Card icon={MdImage} title="Images">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {(data.images ?? []).map((m: any) => (
                <img
                  key={m.id}
                  src={m.url}
                  onClick={() => setPreview(m.url)}
                  style={{
                    width: 140,
                    height: 140,
                    borderRadius: 14,
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </Card>

          <Card icon={MdPictureAsPdf} title="Documents">
            <div style={{ display: "flex", gap: 16 }}>
              {(data.documents ?? []).map((m: any) => (
                <div
                  key={m.id}
                  onClick={() => setPreview(m.url)}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 14,
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <MdPictureAsPdf size={36} />
                </div>
              ))}
            </div>
          </Card>

          {preview && (
            <Modal open onClose={() => setPreview(null)}>
              {preview.endsWith(".pdf") ? (
                <iframe
                  src={preview}
                  style={{ width: "100%", height: "80vh" }}
                />
              ) : (
                <img src={preview} style={{ maxWidth: "100%" }} />
              )}
            </Modal>
          )}
        </div>
      )}
    </Modal>
  );
};

export const AircraftSaleLeaseAvailableForms = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.aircraftsaleleaseAvailable.getAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "aircraft",
        title: "Aircraft",
        accessor: (r: any) => r.aircraftType?.name,
      },
      {
        key: "type",
        title: "Trade Type",
        accessor: (r: any) => (r.tradeType === 1 ? "Sale" : "Lease"),
      },
      { key: "msn", title: "MSN", accessor: (r: any) => r.msn },
      {
        key: "ownerName",
        title: "Owner Name",
        accessor: (r: any) => r.ownerName || r.company?.companyName || "-",
      },
      {
        key: "company",
        title: "Company",
        accessor: (r: any) => r.company?.companyName,
      },
      { key: "capacity", title: "Capacity", accessor: (r: any) => r.capacity },
      {
        key: "status",
        title: "Status",
        accessor: (r: any) => r.confirmStatus,
        render: (_: any, v: any) => (
          <Badge
            label={ConfirmStatusText[v]}
            color={ConfirmStatusColor[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailId(r.id),
              },
              {
                key: "confirm",
                icon: MdCheck,
                color: "#F59E0B",
                onClick: () => setStatusRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Aircraft Sale/Lease Available
        </div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          Manage available aircraft
        </div>
      </div>
      <Table loading={loading} columns={columns} data={data} />

      {detailId && (
        <AircraftSaleLeaseAvailableDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {statusRow && (
        <ConfirmStatusModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
